import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../utils/Inter-Regular-normal.js";
import { getImageDataUrlFromServer } from "../../utils/EmbedFields.js";
import {
  drawJustifiedParagraphs,
  splitHTMLByPageBreaks,
  generateDynamicTable,
} from "./GenerateNewInvoicePDFHelper.js";

const ensureJustified = (htmlText) => {
  if (!htmlText) return htmlText;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlText;

  const paragraphs = tempDiv.querySelectorAll("p");
  paragraphs.forEach((p) => {
    if (
      !p.className.includes("ql-align-") &&
      (!p.style.textAlign || p.style.textAlign === "left")
    ) {
      p.style.textAlign = "justify";
    }
  });

  return tempDiv.innerHTML;
};

function splitTextToWidth(doc, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const testLine = currentLine ? currentLine + " " + word : word;
    const testWidth = doc.getTextWidth(testLine);

    if (testWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

const addFooterToPages = async (
  doc,
  footerImageDataUrl,
  pageWidth,
  pageHeight,
  mainDocumentPageCount
) => {
  let footerHeight = 0;

  const getFooterImageData = async (imageUrl) => {
    let dataUrl = imageUrl;
    if (imageUrl.includes("https://")) {
      dataUrl = await getImageDataUrlFromServer(imageUrl);
    }
    if (!dataUrl) return null;

    const imgProps = doc.getImageProperties(dataUrl);
    const aspectRatio = imgProps.width / imgProps.height;

    const maxHeight = pageHeight * 0.1;
    const maxWidth = pageWidth * 0.9;

    let renderHeight = maxHeight;
    let renderWidth = renderHeight * aspectRatio;

    if (renderWidth > maxWidth) {
      renderWidth = maxWidth;
      renderHeight = renderWidth / aspectRatio;
    }

    const xPos = (pageWidth - renderWidth) / 2;
    const yPos = pageHeight - renderHeight - 20;

    return {
      dataUrl,
      width: renderWidth,
      height: renderHeight,
      x: xPos,
      y: yPos,
    };
  };

  let footerImage = null;
  if (footerImageDataUrl) {
    footerImage = await getFooterImageData(footerImageDataUrl);
    if (footerImage) {
      footerHeight = footerImage.height;
    }
  }

  if (footerImage) {
    for (let i = 1; i <= mainDocumentPageCount; i++) {
      doc.setPage(i);
      doc.addImage(
        footerImage.dataUrl,
        "PNG",
        footerImage.x,
        footerImage.y,
        footerImage.width,
        footerImage.height
      );
    }
  }

  return footerHeight;
};

const generateNewInvoiceTypePDF = async (
  formData,
  headerImageDataUrl,
  footerImageDataUrl,
  cid,
  formattedDate,
  invoiceType,
  action
) => {
  const contentMargin = 72;
  const doc = new jsPDF("p", "pt", [612, 936]);
  const author = formData.closing?.name || "";
  doc.setProperties({
    title: formData.header || "",
    subject: formData.subject || "",
    author: author,
    keywords: "",
  });

  doc.setFont("times", "normal");

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Calculate footer height
  let footerHeight = 0;
  if (footerImageDataUrl) {
    let footerDataUrl = footerImageDataUrl;
    if (footerImageDataUrl.includes("https://")) {
      footerDataUrl = await getImageDataUrlFromServer(footerImageDataUrl);
    }
    if (footerDataUrl) {
      const imgProps = doc.getImageProperties(footerDataUrl);
      footerHeight = (pageWidth * imgProps.height) / imgProps.width;

      const maxFooterHeight = pageHeight * 0.1;
      if (footerHeight > maxFooterHeight) {
        footerHeight = maxFooterHeight;
      }
    }
  }

  // Header image handling
  const MAX_HEADER_HEIGHT = 150;
  const MAX_HEADER_WIDTH = pageWidth;

  let headerImageHeight = 0;

  const drawHeaderImage = async (imageDataUrl) => {
    try {
      const imgProps = doc.getImageProperties(imageDataUrl);
      const imgWidth = imgProps.width;
      const imgHeight = imgProps.height;

      const aspectRatio = imgWidth / imgHeight;

      let renderWidth = MAX_HEADER_WIDTH;
      let renderHeight = renderWidth / aspectRatio;

      if (renderHeight > MAX_HEADER_HEIGHT) {
        renderHeight = MAX_HEADER_HEIGHT;
        renderWidth = renderHeight * aspectRatio;
      }

      const xPos = (pageWidth - renderWidth) / 2;
      const yPos = 0;

      doc.addImage(imageDataUrl, "PNG", xPos, yPos, renderWidth, renderHeight);
      return renderHeight;
    } catch (e) {
      console.error("Error loading image:", e);
      return 50;
    }
  };

  if (headerImageDataUrl && headerImageDataUrl.includes("https://")) {
    const dataUrl = await getImageDataUrlFromServer(headerImageDataUrl);
    headerImageHeight = dataUrl ? await drawHeaderImage(dataUrl) : 50;
  } else if (headerImageDataUrl) {
    headerImageHeight = await drawHeaderImage(headerImageDataUrl);
  } else {
    headerImageHeight = 50;
  }

  let currentY = headerImageHeight + 30;
  const textWidth = pageWidth - 2 * contentMargin;
  const lineHeight = 14;

  const checkPageBreak = (requiredHeight, currentY) => {
    if (currentY + requiredHeight > pageHeight - 40 - footerHeight) {
      doc.addPage();
      return { pageAdded: true, newY: 80 };
    }
    return { pageAdded: false, newY: currentY };
  };

  const renderNotaryInfo = (currentY, notaryInfo) => {
    const result = checkPageBreak(100, currentY);
    let y = result.newY;

    y += 40;

    doc.setFont("times", "normal");
    doc.setFontSize(12);

    doc.text(`Doc. No. ${notaryInfo.docNo || "______"};`, contentMargin, y);
    y += 15;
    doc.text(`Page No. ${notaryInfo.pageNo || "______"};`, contentMargin, y);
    y += 15;
    doc.text(`Book No. ${notaryInfo.bookNo || "______"};`, contentMargin, y);
    y += 15;
    doc.text(`Series of ${notaryInfo.series || "______"}.`, contentMargin, y);

    return y + 15;
  };

  const renderFromAndTo = () => {
    // Regular document flow (acknowledgement, transmittal, legal advice)
    const lineSpacing = 12;

    const fromToY = currentY;
    const columnWidth = (pageWidth - contentMargin) / 2;

    // FROM section
    const fromX = contentMargin;
    doc.setFont("times", "bold").setFontSize(12);
    doc.text("From:", fromX, fromToY);

    let fromY = fromToY + 14;
    let fromLineCount = 0;
    const maxFromLines = 6;

    doc.setFont("times", "normal").setFontSize(12);
    doc.text(formData.from?.name || "-", fromX, fromY);
    fromY += lineSpacing;
    fromLineCount++;

    if (fromLineCount < maxFromLines) {
      const addressText = formData.from?.address || "-";
      const availableWidth = columnWidth - 20;
      const addressLines = splitTextToWidth(doc, addressText, availableWidth);

      const maxAddressLines = Math.min(2, maxFromLines - fromLineCount - 1);
      let displayLines = addressLines.slice(0, maxAddressLines);

      if (addressLines.length > maxAddressLines) {
        if (displayLines.length > 0) {
          const lastLine = displayLines[displayLines.length - 1];
          const maxLastLineWidth = availableWidth - doc.getTextWidth("...");
          let truncatedLine = lastLine;
          while (
            doc.getTextWidth(truncatedLine) > maxLastLineWidth &&
            truncatedLine.length > 0
          ) {
            truncatedLine = truncatedLine.slice(0, -1);
          }
          displayLines[displayLines.length - 1] = truncatedLine + "...";
        }
      }

      displayLines.forEach((line) => {
        if (fromLineCount < maxFromLines) {
          doc.text(line, fromX, fromY);
          fromY += lineSpacing;
          fromLineCount++;
        }
      });
    }

    if (fromLineCount < maxFromLines) {
      const contactText = formData.from?.contact || "-";
      const availableWidth = columnWidth - 20;
      let displayContact = contactText;

      if (doc.getTextWidth(contactText) > availableWidth) {
        const maxContactWidth = availableWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayContact) > maxContactWidth &&
          displayContact.length > 0
        ) {
          displayContact = displayContact.slice(0, -1);
        }
        displayContact += "...";
      }

      doc.text(displayContact, fromX, fromY);
      fromY += lineSpacing;
      fromLineCount++;
    }

    // TO section
    const toX = contentMargin + columnWidth;
    doc.setFont("times", "bold").setFontSize(12);
    doc.text("To:", toX, fromToY);

    let toY = fromToY + 14;
    let toLineCount = 0;
    const maxToLines = 6;

    doc.setFont("times", "normal").setFontSize(12);
    doc.text(formData.to?.name || "-", toX, toY);
    toY += lineSpacing;
    toLineCount++;

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const tinLabel = "TIN no.:";
      const tinLabelWidth = doc.getTextWidth(tinLabel);
      doc.text(tinLabel, toX, toY);
      doc.setFont("times", "normal");
      doc.text(formData.to?.tinNo || "-", toX + tinLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const addressLabel = "Address:";
      const addressLabelWidth = doc.getTextWidth(addressLabel);
      doc.text(addressLabel, toX, toY);

      const addressText = formData.to?.address || "-";
      const availableWidth = columnWidth - 20 - addressLabelWidth - 5;
      const addressLines = splitTextToWidth(doc, addressText, availableWidth);

      let displayLines = addressLines.slice(
        0,
        Math.min(2, maxToLines - toLineCount)
      );
      if (
        addressLines.length > 2 ||
        (toLineCount + displayLines.length >= maxToLines &&
          addressLines.length > displayLines.length)
      ) {
        if (displayLines.length > 0) {
          const lastLine = displayLines[displayLines.length - 1];
          const maxLastLineWidth = availableWidth - doc.getTextWidth("...");
          let truncatedLine = lastLine;
          while (
            doc.getTextWidth(truncatedLine) > maxLastLineWidth &&
            truncatedLine.length > 0
          ) {
            truncatedLine = truncatedLine.slice(0, -1);
          }
          displayLines[displayLines.length - 1] = truncatedLine + "...";
        }
      }

      doc.setFont("times", "normal");
      displayLines.forEach((line, index) => {
        if (toLineCount < maxToLines) {
          if (index === 0) {
            doc.text(line, toX + addressLabelWidth + 5, toY);
          } else {
            doc.text(
              line,
              toX + addressLabelWidth + 5,
              toY + index * lineSpacing
            );
          }
        }
      });

      const addressLineCount = Math.min(
        displayLines.length,
        maxToLines - toLineCount
      );
      toY += addressLineCount * lineSpacing;
      toLineCount += addressLineCount;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const contactLabel = "Contact:";
      const contactLabelWidth = doc.getTextWidth(contactLabel);
      doc.text(contactLabel, toX, toY);

      doc.setFont("times", "normal");
      const contactText = formData.to?.contact || "-";
      const availableContactWidth = columnWidth - 20 - contactLabelWidth - 5;

      let displayContact = contactText;
      if (doc.getTextWidth(contactText) > availableContactWidth) {
        const maxContactWidth = availableContactWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayContact) > maxContactWidth &&
          displayContact.length > 0
        ) {
          displayContact = displayContact.slice(0, -1);
        }
        displayContact += "...";
      }

      doc.text(displayContact, toX + contactLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    if (toLineCount < maxToLines) {
      doc.setFont("times", "bold");
      const repLabel = "Rep.:";
      const repLabelWidth = doc.getTextWidth(repLabel);
      doc.text(repLabel, toX, toY);

      doc.setFont("times", "normal");
      const repText = formData.to?.rep || "-";
      const availableRepWidth = columnWidth - 20 - repLabelWidth - 5;

      let displayRep = repText;
      if (doc.getTextWidth(repText) > availableRepWidth) {
        const maxRepWidth = availableRepWidth - doc.getTextWidth("...");
        while (
          doc.getTextWidth(displayRep) > maxRepWidth &&
          displayRep.length > 0
        ) {
          displayRep = displayRep.slice(0, -1);
        }
        displayRep += "...";
      }

      doc.text(displayRep, toX + repLabelWidth + 5, toY);
      toY += lineSpacing;
      toLineCount++;
    }

    return Math.max(fromY, toY + 30);
  };

  // Check if this is a sec-res type that only needs notarization content
  const isSecResOnly =
    invoiceType === "secres" && formData.notarization?.content;

  if (isSecResOnly) {
    currentY = renderFromAndTo();
    // For sec-res: Only render the notarization content with justification
    const sections = splitHTMLByPageBreaks(formData.notarization.content);

    for (let i = 0; i < sections.length; i++) {
      if (i > 0) {
        doc.addPage();
        currentY = 60;
      }

      currentY = drawJustifiedParagraphs(
        doc,
        ensureJustified(sections[i]),
        contentMargin,
        currentY,
        textWidth,
        lineHeight,
        pageHeight,
        40,
        0,
        footerHeight,
        0,
        40,
        5,
        40
      );
    }

    if (formData.notarization?.notaryInfo) {
      currentY = renderNotaryInfo(currentY, formData.notarization.notaryInfo);
    }

    // Store page count for footer
    const mainDocumentPageCount = doc.getNumberOfPages();

    // Add footers to all pages
    if (footerImageDataUrl) {
      await addFooterToPages(
        doc,
        footerImageDataUrl,
        pageWidth,
        pageHeight,
        mainDocumentPageCount
      );
    }
  } else {
    currentY = renderFromAndTo();
    // Date text
    doc.setFontSize(12);
    doc.text(`${formattedDate}`, contentMargin, currentY);
    currentY += 40;

    // Header text with letter spacing
    const headerText = `${formData.header}`.toUpperCase();
    const fontSize = 14;
    const letterSpacing = 1.5;
    const currentYPosition = currentY;

    doc.setFont("times", "bold");
    doc.setFontSize(fontSize);

    const totalTextWidth =
      headerText
        .split("")
        .reduce((acc, char) => acc + doc.getTextWidth(char), 0) +
      (headerText.length - 1) * letterSpacing;

    const startX = (pageWidth - totalTextWidth) / 2;

    let x = startX;
    for (let i = 0; i < headerText.length; i++) {
      const char = headerText[i];
      doc.text(char, x, currentYPosition);
      const charWidth = doc.getTextWidth(char);
      x += charWidth + letterSpacing;
    }

    const underlineY = currentYPosition + 1;
    doc.line(startX, underlineY, startX + totalTextWidth, underlineY);

    // Subject line
    if (formData.subject && formData.subject.trim() !== "") {
      currentY += 40;
      doc.setFont("times", "bolditalic");
      doc.text(`InRe: ${formData.subject}`, contentMargin, currentY);
    }

    if (formData.letterOpening && formData.letterOpening.trim() !== "") {
      currentY += 30;
      doc.setFont("times", "normal");
      doc.text(`${formData.letterOpening}`, contentMargin, currentY);
    }

    // Body text before table
    currentY += 30;
    doc.setFont("times", "normal");
    doc.setFontSize(12);

    if (formData.body1 && formData.body1.trim() !== "") {
      const body1Sections = splitHTMLByPageBreaks(formData.body1);

      for (let i = 0; i < body1Sections.length; i++) {
        if (i > 0) {
          doc.addPage();
          currentY = 30;
        }

        currentY = drawJustifiedParagraphs(
          doc,
          ensureJustified(body1Sections[i]),
          contentMargin,
          currentY,
          textWidth,
          lineHeight,
          pageHeight,
          40,
          0,
          footerHeight,
          0,
          40,
          5,
          40
        );
      }
    }

    // Generate dynamic table if applicable
    if (formData.tableIncluded && formData.items && formData.items.length > 0) {
      currentY = generateDynamicTable(
        doc,
        formData,
        currentY,
        contentMargin,
        pageWidth
      );
    }

    // Body text after table
    if (formData.body2 && formData.body2.trim() !== "") {
      const body2Sections = splitHTMLByPageBreaks(formData.body2);

      for (let i = 0; i < body2Sections.length; i++) {
        if (i > 0) {
          doc.addPage();
          currentY = 30;
        }

        currentY = drawJustifiedParagraphs(
          doc,
          ensureJustified(body2Sections[i]),
          contentMargin,
          currentY + 10,
          textWidth,
          lineHeight,
          pageHeight,
          40,
          0,
          footerHeight,
          0,
          40,
          5,
          40
        );
      }
    }

    currentY += 30;

    // Closing section
    const closingSectionHeight = 30 + 15;
    if (currentY + closingSectionHeight > pageHeight - 40 - footerHeight) {
      doc.addPage();
      currentY = 30;
    }

    if (formData.closing?.complimentary) {
      doc.text(formData.closing.complimentary, contentMargin, currentY);
      currentY += 30;
    }

    if (formData.closing?.name && formData.closing.name.trim() !== "") {
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      const nameUpper = formData.closing.name.toUpperCase();
      const nameWidth = doc.getTextWidth(nameUpper);

      doc.text(nameUpper, contentMargin, currentY);
      currentY += 5;

      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(
        contentMargin,
        currentY,
        contentMargin + nameWidth + 10,
        currentY
      );
      currentY += 15;
    }

    if (formData.closing?.position) {
      doc.setFont("times", "normal");
      doc.text(formData.closing.position, contentMargin, currentY);
      currentY += 15;
    }

    if (formData.closing?.company) {
      doc.text(formData.closing.company, contentMargin, currentY);
    }

    // Store the page count before adding notarization
    const mainDocumentPageCount = doc.getNumberOfPages();

    // Add notarization content if it exists
    // This now works for BOTH "ack" and "secres" types with the single content field
    if (
      (invoiceType === "ack" || invoiceType === "secres") &&
      formData.notarization?.content &&
      formData.notarization.content.trim() !== ""
    ) {
      // Add a new page for notarization
      doc.addPage();
      currentY = 80;

      // Split notarization content by page breaks and render with justification
      const notarySections = splitHTMLByPageBreaks(
        formData.notarization.content
      );

      for (let i = 0; i < notarySections.length; i++) {
        if (i > 0) {
          doc.addPage();
          currentY = 80;
        }

        // Get the actual current page number from the document
        const currentPageNumber = doc.internal.getNumberOfPages();
        let ackTexts = notarySections[i] || "";

        // Replace @page with the current page number
        ackTexts = ackTexts.replace(/@page/g, currentPageNumber);

        currentY = drawJustifiedParagraphs(
          doc,
          ensureJustified(ackTexts),
          contentMargin,
          currentY,
          textWidth,
          lineHeight,
          pageHeight,
          40,
          0,
          0,
          0,
          40,
          5,
          40
        );
      }
      if (formData.notarization?.notaryInfo) {
        currentY = renderNotaryInfo(currentY, formData.notarization.notaryInfo);
      }
    }
    // Add footers to all pages
    if (footerImageDataUrl) {
      await addFooterToPages(
        doc,
        footerImageDataUrl,
        pageWidth,
        pageHeight,
        mainDocumentPageCount
      );
    }
  }

  // Add page numbers
  const pageCount = doc.getNumberOfPages();
  const rightAlignX = pageWidth - contentMargin;

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(10);

    let yPosition = pageHeight - 10;

    doc.text(`Page ${i} of ${pageCount}`, rightAlignX, yPosition, {
      align: "right",
    });
  }

  const pdfBlobUrl = doc.output("bloburl");

  if (action === "download") {
    const tempCid = cid ? cid : "CID000XX";
    const filename = `${tempCid}_${formData.invoiceNo}.pdf`;
    doc.save(`${filename}`);
  }

  return pdfBlobUrl;
};

export default generateNewInvoiceTypePDF;
